import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";

// Initialize Firestore
const db = getFirestore();

class Facture {
  constructor(
    fournisseurId,
    restaurantId,
    numero,
    achatHTVA,
    tvaDeductible,
    achatTTC,
    montantPaye,
    statut,
    resteAPayer,
    modeDePaiement,
    paymentnum,
    retouralasource , // New field
    date ,
  ) {
    this.fournisseurId = fournisseurId;
    this.restaurantId = restaurantId;
    this.numero = numero;
    this.achatHTVA = achatHTVA;
    this.tvaDeductible = tvaDeductible;
    this.achatTTC = achatTTC;
    this.montantPaye = montantPaye;
    this.statut = statut;
    this.resteAPayer = resteAPayer;
    this.modeDePaiement = modeDePaiement;
    this.paymentnum = paymentnum;
    this.retouralasource = retouralasource;
   this.date = new Date().toLocaleDateString(); // Automatically gets today's date in MM/DD/YYYY format

    this.id = ""; // Assigned after saving
  }

  // Convert object to Firestore format
  toFirestore() {
    return {
      fournisseurId: this.fournisseurId,
      restaurantId: this.restaurantId,
      numero: this.numero,
      achatHTVA: this.achatHTVA,
      tvaDeductible: this.tvaDeductible,
      achatTTC: this.achatTTC,
      montantPaye: this.montantPaye,
      statut: this.statut,
      resteAPayer: this.resteAPayer,
      modeDePaiement: this.modeDePaiement,
      paymentnum: this.paymentnum,
      retouralasource: this.retouralasource,
      date: this.date,
    };
  }

  async saveFacture() {
    const factureRef = doc(collection(db, "factures"));
    this.id = factureRef.id;
    await setDoc(factureRef, this.toFirestore());
  }

  static async getFactureById(factureId) {
    const factureRef = doc(db, "factures", factureId);
    const factureSnapshot = await getDoc(factureRef);
    if (!factureSnapshot.exists()) throw new Error("Facture not found");
    return { id: factureSnapshot.id, ...factureSnapshot.data() };
  }

  static async updateFacture(factureId, fournisseurId, updatedData) {
    const factureRef = doc(db, "factures", factureId);
    const factureSnapshot = await getDoc(factureRef);

    if (!factureSnapshot.exists()) throw new Error("Facture not found");

    const factureData = factureSnapshot.data();
    if (factureData.fournisseurId !== fournisseurId) {
      throw new Error("Fournisseur ID does not match");
    }

    await updateDoc(factureRef, updatedData);
    console.log("Facture updated successfully");
  }

  static async deleteFacture(factureId) {
    await deleteDoc(doc(db, "factures", factureId));
  }

  static async getAllFactures() {
    const factures = [];
    const querySnapshot = await getDocs(collection(db, "factures"));
    querySnapshot.forEach((doc) => {
      factures.push({ id: doc.id, ...doc.data() });
    });
    return factures;
  }

  static async getFacturesByFournisseurAndRestaurant(fournisseurId, restaurantId) {
    const q = query(
      collection(db, "factures"),
      where("fournisseurId", "==", fournisseurId),
      where("restaurantId", "==", restaurantId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  static async getFacturesByFournisseurId(fournisseurId) {
    const q = query(collection(db, "factures"), where("fournisseurId", "==", fournisseurId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  static async getTotalDesimpyéByRestaurantId(restaurantId, selectedMonth, selectedYear) {
    const q = query(collection(db, "factures"), where("restaurantId", "==", restaurantId));
    const querySnapshot = await getDocs(q);

    let totalResteAPayer = 0;

    querySnapshot.forEach((doc) => {
      const factureData = doc.data();
      const factureDate = factureData.date?.toDate?.() || new Date(factureData.date);

      if (
        factureDate.getMonth() === selectedMonth &&
        factureDate.getFullYear() === selectedYear
      ) {
        totalResteAPayer += Number(factureData.resteAPayer);
      }
    });

    return new Intl.NumberFormat('fr-BI', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(Number(totalResteAPayer.toFixed(3)));
  }

  static async getTotalAchatHTVAByRestaurantId(restaurantId, selectedMonth, selectedYear) {
    try {
      const q = query(collection(db, "factures"), where("restaurantId", "==", restaurantId));
      const querySnapshot = await getDocs(q);

      let totalAchatHTVA = 0;

      querySnapshot.forEach((doc) => {
        const factureData = doc.data();
        const factureDate = factureData.date?.toDate?.() || new Date(factureData.date);

        if (
          factureDate.getMonth() === selectedMonth &&
          factureDate.getFullYear() === selectedYear
        ) {
          totalAchatHTVA += Number(factureData.achatHTVA ?? 0);
        }
      });

      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(Number(totalAchatHTVA.toFixed(3)));
    } catch (error) {
      console.error("Error getting filtered total Achat HTVA:", error.message);
      throw error;
    }
  }

  static async getTotalTVADeductibleByRestaurantId(restaurantId, selectedMonth, selectedYear) {
    try {
      const q = query(collection(db, "factures"), where("restaurantId", "==", restaurantId));
      const querySnapshot = await getDocs(q);

      let totalTVADeductible = 0;

      querySnapshot.forEach((doc) => {
        const factureData = doc.data();
        const factureDate = factureData.date?.toDate?.() || new Date(factureData.date);

        if (
          factureDate.getMonth() === selectedMonth &&
          factureDate.getFullYear() === selectedYear
        ) {
          totalTVADeductible += Number(factureData.tvaDeductible ?? 0);
        }
      });

      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(Number(totalTVADeductible.toFixed(3)));
    } catch (error) {
      console.error("Error getting filtered total TVA Deductible:", error.message);
      throw error;
    }
  }

  static async getTotalTTCByRestaurantId(restaurantId, selectedMonth, selectedYear) {
    try {
      const q = query(collection(db, "factures"), where("restaurantId", "==", restaurantId));
      const querySnapshot = await getDocs(q);

      let totalTTC = 0;

      querySnapshot.forEach((doc) => {
        const factureData = doc.data();
        const factureDate = factureData.date?.toDate?.() || new Date(factureData.date);

        if (
          factureDate.getMonth() === selectedMonth &&
          factureDate.getFullYear() === selectedYear
        ) {
          totalTTC += Number(factureData.achatTTC ?? 0);
        }
      });

      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(Number(totalTTC.toFixed(3)));
    } catch (error) {
      console.error("Error getting filtered total Achat TTC:", error.message);
      throw error;
    }
  }
}

export default Facture;
